import { useRef } from 'react';
import type { UploadStatus } from '../platform/uploads.js';
import { Icon } from './icons.js';

/**
 * Photo capture strip — design v1.1 §4.1. Dumb by design: files in, chips out, every
 * upload state visible. The compression/upload orchestration lives in the intake
 * screen, which is what keeps this component harness-testable without a canvas.
 *
 * P2's grave: ONE gallery action attaches many (`multiple`), and the camera input
 * returns to the shutter, not to the form.
 */

export interface PhotoDraft {
  /** The R2 object key once minted; a local id before that. Stable for React. */
  id: string;
  previewUrl: string;
  /** `expired` arrives from the PERSISTENT queue only (offline increment 2): an op
   * nobody managed to send within thirty days. It is deliberately not folded into
   * `failed` — nothing rejected it, it simply waited too long, and the two want
   * different words from the person reading the chip. */
  status: UploadStatus | 'compressing' | 'expired';
  message?: string | undefined;
}

export interface PhotoStripProps {
  photos: PhotoDraft[];
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  /**
   * Overrides the Gallery button's label. The BOX DETAIL says "Add N photos as separate items"
   * (maintainer, 5 Aug 2026) because on that screen picking from the gallery is how a delivery
   * gets catalogued, and "Gallery" named the source of the files rather than the act.
   *
   * ⚠️ A LABEL, NOT A BEHAVIOUR. Confirmed with the maintainer before shipping: the photos
   * still attach to the box, one photo does not become one item. If that ever changes, this
   * prop is the first thing to delete — a label promising a flow the code does not have is
   * worse than the name it replaced.
   */
  galleryLabel?: string | undefined;
}

const STATUS_LABEL: Record<PhotoDraft['status'], string> = {
  compressing: 'compressing…',
  queued: 'waiting…',
  uploading: 'uploading…',
  failed: 'failed',
  expired: 'waiting too long',
  done: '✓',
};

/**
 * SPLIT IN TWO (0.1.177). The box detail puts the BUTTONS in the items header and the
 * THUMBNAILS below it — the strip used to live whole inside the sticky header, and every
 * upload inflated the header's height, pushing the list and the sticky arithmetic around
 * ("the photos upload progress gets put in the item title row which expands it like crazy").
 * `PhotoStrip` still renders both together for the intake form, which has no sticky header
 * to protect.
 */
export function PhotoStripButtons({
  onAdd,
  galleryLabel,
}: {
  onAdd: (files: File[]) => void;
  galleryLabel?: string | undefined;
}): JSX.Element {
  const galleryInput = useRef<HTMLInputElement | null>(null);
  const cameraInput = useRef<HTMLInputElement | null>(null);

  const takeFiles = (input: HTMLInputElement | null): void => {
    if (!input?.files || input.files.length === 0) return;
    onAdd(Array.from(input.files));
    // Same file re-selectable (retake after remove) — a stale FileList would silently
    // swallow the second attempt at the same photo.
    input.value = '';
  };

  return (
    <>
      <button type="button" className="chip" onClick={() => cameraInput.current?.click()}>
        Camera
      </button>
      <button type="button" className="chip" onClick={() => galleryInput.current?.click()}>
        {galleryLabel ?? 'Gallery'}
      </button>
      {/* The inputs are the mechanism, the buttons are the UI: a styled file input is
          not reliably tappable at thumb speed on Android, a button always is. */}
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        aria-label="Take photo"
        onChange={(e) => {
          takeFiles(e.currentTarget);
        }}
      />
      <input
        ref={galleryInput}
        type="file"
        accept="image/*"
        multiple
        hidden
        aria-label="Choose photos"
        onChange={(e) => {
          takeFiles(e.currentTarget);
        }}
      />
    </>
  );
}

/**
 * THE ADD FRAME (0.1.502; maintainer, 9 Sep 2026: the desk item keeps the redesign's photo
 * section — "one big hero picture … little photo frames below it and a little photo frame, an
 * empty box with a plus inside"). A 52px dashed frame with a plus, the size of the strip's thumbs,
 * so the door to the next photo sits where the next photo will appear. ONE `image/*` input with no
 * `capture`, exactly the phone hero's add tile (item-detail.tsx `ItemHero`): the OS offers camera
 * or library, so the Camera / Gallery pair this replaced on the desk is not needed there.
 * `PhotoStripButtons` stays for the intake form and the check-in sheet, which still show the pair.
 */
export function PhotoAddFrame({ onAdd, label = 'Add photos' }: { onAdd: (files: File[]) => void; label?: string }): JSX.Element {
  const input = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <button type="button" className="photo-add-frame" aria-label={label} onClick={() => input.current?.click()}>
        <Icon name="plus" size={20} />
      </button>
      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        hidden
        aria-label={label}
        onChange={(e) => {
          const el = e.currentTarget;
          if (el.files && el.files.length > 0) onAdd(Array.from(el.files));
          el.value = '';
        }}
      />
    </>
  );
}

export function PhotoStripThumbs({
  photos,
  onRemove,
  onRetry,
}: {
  photos: PhotoDraft[];
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}): JSX.Element {
  return (
    <div className="photo-strip-chips">
      {photos.map((photo) => (
        <figure key={photo.id} className="photo-chip">
          <img src={photo.previewUrl} alt="" />
          <figcaption className={`photo-chip-status photo-chip-${photo.status}`} role="status">
            {STATUS_LABEL[photo.status]}
          </figcaption>
          {photo.status === 'failed' && (
            <button type="button" className="photo-chip-retry" onClick={() => onRetry(photo.id)}>
              Retry
            </button>
          )}
          <button
            type="button"
            className="photo-chip-remove"
            aria-label="Remove photo"
            onClick={() => onRemove(photo.id)}
          >
            ×
          </button>
        </figure>
      ))}
    </div>
  );
}

export function PhotoStrip({ photos, onAdd, onRemove, onRetry, galleryLabel }: PhotoStripProps): JSX.Element {
  return (
    <div className="photo-strip">
      <PhotoStripThumbs photos={photos} onRemove={onRemove} onRetry={onRetry} />
      <p className="photo-strip-actions">
        <PhotoStripButtons onAdd={onAdd} galleryLabel={galleryLabel} />
      </p>
    </div>
  );
}

/**
 * THE PHONE'S TWO ADD BUTTONS (0.1.522; maintainer, 1 Oct 2026: "on mobile replace the two dashed
 * box buttons with the same green buttons as on desktop and place them above the search box") —
 * moved BELOW the search the same evening (0.1.523: "move … to below the search bar"), and the Items
 * tab now passes `compact` too ("the same smaller size"). The dashed add-split ("New item" · "Photos as new items", 0.1.347) retires on all three lists
 * that drew it — the Items tab, a box, a reservation — for the desk's own pair: filled primaries,
 * the "+" in front of both (the same day's label ruling).
 *
 * `compact` is the scoped lists' smaller size: inside a box or a reservation the section title and
 * the garment count already sit above the search, so the pair is shorter there (maintainer, same
 * day). ONE `image/*` input, no `capture`: the OS offers camera or library, exactly as the dashed
 * zone did. Drag-and-drop was the zone's desk-era affordance; on a phone a tap is the gesture.
 * Hoisted at the third copy (items.tsx, boxes.tsx, bookings.tsx).
 *
 * 0.1.526 (maintainer, 2 Oct 2026): "+ New item" (the "Add" dropped everywhere), and an optional
 * THIRD door — `onExisting`, "+ Existing item" — for the reservation and the outfit, where picking
 * from the catalogue is as common as minting (0.1.527 swapped which kind is solid — see below).
 *
 * 0.1.528 (maintainer, 3 Oct 2026: "put the '+existing X' at the right side and the '+new x' button
 * left"): the pick door moved from the FRONT of the three to the END — "+ New item" · "+ Photos as
 * new items" (both outlined) · "+ Existing item" (solid, right). Every pair in the app now reads the
 * same way round: creates on the left, the one primary — the pick — on the right (`SlotPair`).
 *
 * ⚠️ REVERSED THE SAME DAY (0.1.528, second pass; maintainer, 3 Oct 2026: "the existing outfit
 * button should be left and the new button should be right. This should be a rule for anywhere that
 * uses these two buttons next to each other. And the existing item button should also be left under
 * the search bar"). The three doors now read "+ Existing item" (solid primary, LEFT) · "+ New item"
 * · "+ Photos as new items" (both outlined). The paragraph above is the superseded order, kept as
 * history; the rule is `SlotPair`'s.
 */
export function PhotoAddButtons({
  onNew,
  onPhotos,
  onExisting,
  compact = false,
  disabled = false,
}: {
  onNew: () => void;
  onPhotos: (files: File[]) => void;
  onExisting?: (() => void) | undefined;
  compact?: boolean;
  disabled?: boolean;
}): JSX.Element {
  const input = useRef<HTMLInputElement | null>(null);
  const three = onExisting !== undefined;
  const cls = `photo-add-buttons${compact ? ' photo-add-buttons-compact' : ''}${three ? ' photo-add-buttons-three' : ''}`;
  /*
   * ⚠️ WITH THE PICK DOOR PRESENT, PICKING IS THE PRIMARY (0.1.527; maintainer, 2 Oct 2026: "the
   * +existing item need to be full green button, and the +new item and +photos as new items need
   * to get the green outline green text design, because creating a new item is secondary, and
   * picking existing items is primary"). 0.1.526 had it the other way round. Without a pick door
   * (box detail, the Items tab) the two creates stay solid — they are the only doors there.
   */
  const createCls = three ? 'btn-outline-accent chip' : 'btn-primary chip';
  return (
    <div className={cls}>
      {/* THE PICK DOOR FIRST, AT THE LEFT (0.1.528, second pass) — the solid primary of the row. */}
      {three && (
        <button type="button" className="btn-primary chip" disabled={disabled} onClick={onExisting}>
          + Existing item
        </button>
      )}
      <button type="button" className={createCls} disabled={disabled} onClick={onNew}>
        + New item
      </button>
      {/* TWO LINES ON A PHONE (0.1.527: "make two text rows within the '+photos as new items' button,
          so that all the text is visible") — `.photo-add-two-line`, three-door row only. */}
      <button type="button" className={`${createCls}${three ? ' photo-add-two-line' : ''}`} disabled={disabled} onClick={() => input.current?.click()}>
        + Photos as new items
      </button>
      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        hidden
        aria-label="Photos as new items"
        onChange={(e) => {
          const el = e.currentTarget;
          if (el.files && el.files.length > 0) onPhotos(Array.from(el.files));
          el.value = '';
        }}
      />
    </div>
  );
}
